/**
 * Participant expression frames (#3842, P3 of #3837).
 *
 * The upper face of a puppet, tracked from the participant's own camera and sent
 * to peers over the WebRTC data channel. MediaPipe's FaceLandmarker emits exactly
 * the ARKit blendshape names the rig already carries, so this is a channel
 * selection and a wire format — not a mapping problem.
 *
 * ## The mouth is not here, and that is the whole design
 *
 * A data channel has no jitter buffer. Anything sent on it arrives on a different
 * schedule from the voice, so a mouth shape sent this way would drift against the
 * audio — the one failure the puppet architecture exists to avoid (FunctionalSpec
 * §3). The mouth stays derived from the audio each listener is playing.
 *
 * These channels tolerate that drift: nobody can see that an eyebrow is 80 ms
 * early. So the packet carries brows, eyes, blink, gaze, cheeks, nose and head
 * pose — and `assertNoMouthChannels` makes adding a mouth channel a test failure
 * rather than a subtle sync bug.
 *
 * ## Wire cost
 *
 * 50 bytes per frame at 30 Hz = **1.5 KB/s** per tracked participant — v1 was 30 B
 * (900 B/s); the v2 mouth block (#3840) spent the rest of the 1.5 KB/s budget, and that
 * figure is payload only, before SCTP/DTLS framing. Binary rather than JSON because the
 * same values as JSON numbers would be several times over budget for the same information.
 */

/**
 * The tracked channels, in wire order. Order IS the format: an index into this
 * array is a byte offset in the packet, so entries may be appended (with a
 * version bump) but never reordered or removed.
 */
export const PARTICIPANT_EXPRESSION_CHANNELS = [
  // Brows — the single most legible signal in the upper face.
  'browDownLeft', 'browDownRight', 'browInnerUp', 'browOuterUpLeft', 'browOuterUpRight',
  // Lids.
  'eyeBlinkLeft', 'eyeBlinkRight',
  'eyeSquintLeft', 'eyeSquintRight',
  'eyeWideLeft', 'eyeWideRight',
  // Gaze, as ARKit's four-per-eye decomposition rather than a vector.
  'eyeLookInLeft', 'eyeLookOutLeft', 'eyeLookUpLeft', 'eyeLookDownLeft',
  'eyeLookInRight', 'eyeLookOutRight', 'eyeLookUpRight', 'eyeLookDownRight',
  // Cheeks and nose — `cheekPuff` is deliberately absent; it is a mouth action.
  'cheekSquintLeft', 'cheekSquintRight',
  'noseSneerLeft', 'noseSneerRight',
] as const;

/**
 * The mouth, carried only when the participant's camera is actually tracking it (#3840).
 *
 * ## Why this is a SEPARATE list, and why the guard above still stands
 *
 * The upper-face list is unconditional: those channels cannot desynchronise from anything,
 * because nothing else is driving them. The mouth is the opposite — it is the one part of the
 * face that has to agree with a sound, and `assertNoMouthChannels` exists so nobody adds a
 * mouth channel to the always-on set by accident.
 *
 * That guard is kept, on that list. This list is the deliberate exception, and it carries an
 * obligation the other does not: whoever RENDERS these must align them to the audio the
 * listener is hearing, because landmarks reach the peer without passing through the jitter
 * buffer the voice does — 40-150 ms of adaptive playout delay that would otherwise put the
 * mouth ahead of the words. See `MouthFrameBuffer`.
 *
 * ## Why the whole ARKit mouth set rather than a chosen few
 *
 * MediaPipe emits all 52 ARKit blendshapes in one pass and the MetaHuman rigs carry all 51 as
 * morphs, so sending the mouth is deleting a filter rather than building a feature. It is still
 * not free: at 30 Hz every appended byte costs 30 B/s, and the packet has a stated ceiling of
 * 1500 B/s that predates this list.
 *
 * So these twenty are the ones that carry visible ARTICULATION, and the ceiling is respected
 * rather than raised. Deliberately dropped: `jawForward`, `jawLeft`, `jawRight`, `mouthLeft`,
 * `mouthRight` (lateral motion, essentially absent from speech), `mouthDimpleLeft/Right` (smile
 * detail already implied by the smile channels) and `cheekPuff` (not a speech action at all).
 * Adding any of them back means raising the ceiling on purpose, which is the point of spelling
 * the trade out here.
 *
 * Note these are the ARKit shapes (`jawOpen`, `mouthPucker`), NOT the `viseme_*` morphs the
 * audio path drives. They are different targets on the same rig, so exactly one of the two may
 * write in a given frame — the renderer, not this file, enforces that.
 */
export const PARTICIPANT_MOUTH_CHANNELS = [
  // Aperture — the single largest speech signal.
  'jawOpen', 'mouthClose',
  // Rounding.
  'mouthFunnel', 'mouthPucker',
  // Spread and its opposite.
  'mouthSmileLeft', 'mouthSmileRight', 'mouthFrownLeft', 'mouthFrownRight',
  'mouthStretchLeft', 'mouthStretchRight',
  // Lip roll and shrug — what separates a /p/ from an /f/ from a /th/.
  'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower', 'mouthShrugUpper',
  'mouthPressLeft', 'mouthPressRight',
  // Independent lip elevation, which is what makes a consonant read as one.
  'mouthLowerDownLeft', 'mouthLowerDownRight', 'mouthUpperUpLeft', 'mouthUpperUpRight',
] as const;

export type ParticipantMouthChannel = typeof PARTICIPANT_MOUTH_CHANNELS[number];
export const PARTICIPANT_MOUTH_CHANNEL_COUNT = PARTICIPANT_MOUTH_CHANNELS.length;

export type ParticipantExpressionChannel = typeof PARTICIPANT_EXPRESSION_CHANNELS[number];
export const PARTICIPANT_EXPRESSION_CHANNEL_COUNT = PARTICIPANT_EXPRESSION_CHANNELS.length;

/**
 * Prefixes a tracked channel may never start with. `cheekPuff` is named in full
 * because it shares the `cheek` prefix with two channels that are legitimate.
 */
export const FORBIDDEN_EXPRESSION_PREFIXES = ['mouth', 'jaw', 'viseme', 'tongue'] as const;
export const FORBIDDEN_EXPRESSION_NAMES = ['cheekPuff'] as const;

/**
 * Throws if a channel list contains anything that belongs to the mouth. Called by
 * the contract test; exported so any future producer can assert on itself.
 */
export const assertNoMouthChannels = (channels: readonly string[]): void => {
  for (const channel of channels) {
    const lower = channel.toLowerCase();
    for (const prefix of FORBIDDEN_EXPRESSION_PREFIXES) {
      if (lower.startsWith(prefix)) {
        throw new Error(
          `ParticipantExpression: '${channel}' is a mouth channel. The mouth is derived from audio ` +
          `on the receiving side; sending it over the data channel would drift against the voice.`,
        );
      }
    }
    if ((FORBIDDEN_EXPRESSION_NAMES as readonly string[]).includes(channel)) {
      throw new Error(`ParticipantExpression: '${channel}' is a mouth action and must not be tracked.`);
    }
  }
};

/** Head rotation in degrees, clamped to this before quantisation. */
export const PARTICIPANT_EXPRESSION_HEAD_LIMIT_DEG = 90;

/** Decoded frame — weights 0..1 by channel name, head angles in degrees. */
export interface ParticipantExpressionFrame {
  /**
   * The tracked mouth, present only while the sender's camera is tracking it (#3840).
   *
   * Absent means "no mouth data", NOT "mouth closed" — a renderer that cannot tell the two
   * apart holds a rest pose through every sentence from a camera-off participant, instead of
   * falling back to the audio-driven mouth.
   *
   * These are ARKit shapes and the audio path drives `viseme_*`. Both exist on the rig, so a
   * renderer must pick one per frame rather than letting them sum.
   */
  mouth?: Partial<Record<ParticipantMouthChannel, number>>;

  /** Channel name → 0..1. Absent channels are 0. */
  weights: Partial<Record<ParticipantExpressionChannel, number>>;
  /** Degrees. Positive yaw = turning to the participant's left. */
  headYaw: number;
  headPitch: number;
  headRoll: number;
  /** Wraps at 65536; used to drop out-of-order frames. */
  sequence: number;
}

export const PARTICIPANT_EXPRESSION_MAGIC = 0xe1;
/**
 * Bumped to 2 for the appended mouth block (#3840). A peer running v1 fails the version check
 * and drops the packet rather than misreading it: the head bytes moved, so a v1 decoder would
 * otherwise read mouth weights as head angles. Losing tracking across a mixed deployment is the
 * correct failure; a puppet twisting its neck because it misparsed a lip is not.
 */
export const PARTICIPANT_EXPRESSION_VERSION = 2;
/** magic + version + seq(2) + channels + head(3) */
export const PARTICIPANT_EXPRESSION_PACKET_BYTES =
  4 + PARTICIPANT_EXPRESSION_CHANNEL_COUNT + PARTICIPANT_MOUTH_CHANNEL_COUNT + 3;
/** Byte offset of the mouth block. */
const MOUTH_OFFSET = 4 + PARTICIPANT_EXPRESSION_CHANNEL_COUNT;
/** Byte offset of the three head angles, after both weight blocks. */
const HEAD_OFFSET = MOUTH_OFFSET + PARTICIPANT_MOUTH_CHANNEL_COUNT;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Encode a frame. Non-finite inputs become 0 rather than poisoning the packet. */
export const encodeParticipantExpression = (frame: ParticipantExpressionFrame): Uint8Array => {
  const out = new Uint8Array(PARTICIPANT_EXPRESSION_PACKET_BYTES);
  out[0] = PARTICIPANT_EXPRESSION_MAGIC;
  out[1] = PARTICIPANT_EXPRESSION_VERSION;
  const seq = ((frame.sequence % 65536) + 65536) % 65536;
  out[2] = (seq >> 8) & 0xff;
  out[3] = seq & 0xff;
  PARTICIPANT_EXPRESSION_CHANNELS.forEach((channel, i) => {
    const raw = frame.weights[channel];
    const v = typeof raw === 'number' && Number.isFinite(raw) ? clamp01(raw) : 0;
    out[4 + i] = Math.round(v * 255);
  });
  // A frame with no mouth (the sender is not tracking it) writes zeros, which decode reads
  // back as an absent mouth rather than a closed one — see `hasMouth`.
  PARTICIPANT_MOUTH_CHANNELS.forEach((channel, i) => {
    const raw = frame.mouth?.[channel];
    const v = typeof raw === 'number' && Number.isFinite(raw) ? clamp01(raw) : 0;
    out[MOUTH_OFFSET + i] = Math.round(v * 255);
  });
  const scale = 127 / PARTICIPANT_EXPRESSION_HEAD_LIMIT_DEG;
  const head = [frame.headYaw, frame.headPitch, frame.headRoll];
  head.forEach((deg, i) => {
    const safe = Number.isFinite(deg) ? deg : 0;
    const clamped = Math.max(-PARTICIPANT_EXPRESSION_HEAD_LIMIT_DEG, Math.min(PARTICIPANT_EXPRESSION_HEAD_LIMIT_DEG, safe));
    out[HEAD_OFFSET + i] = Math.round(clamped * scale) & 0xff;
  });
  return out;
};

/** True if a payload looks like an expression packet — checked before JSON parsing. */
export const isParticipantExpressionPacket = (data: unknown): data is Uint8Array => {
  if (!(data instanceof Uint8Array)) return false;
  return data.length === PARTICIPANT_EXPRESSION_PACKET_BYTES
    && data[0] === PARTICIPANT_EXPRESSION_MAGIC
    && data[1] === PARTICIPANT_EXPRESSION_VERSION;
};

/** Decode a packet, or null if it is not one (wrong magic, version or length). */
export const decodeParticipantExpression = (data: Uint8Array): ParticipantExpressionFrame | null => {
  if (!isParticipantExpressionPacket(data)) return null;
  const weights: Partial<Record<ParticipantExpressionChannel, number>> = {};
  PARTICIPANT_EXPRESSION_CHANNELS.forEach((channel, i) => {
    const v = data[4 + i]!;
    if (v !== 0) weights[channel] = v / 255;
  });
  // An all-zero mouth block means the sender sent no mouth, not that their mouth is shut: a
  // real tracked mouth always carries *something* (`mouthClose` and the lip shapes are never
  // all zero on a live face). Distinguishing them is what lets the renderer fall back to the
  // audio mouth instead of holding a rest pose through a whole sentence.
  const mouth: Partial<Record<ParticipantMouthChannel, number>> = {};
  let hasMouth = false;
  PARTICIPANT_MOUTH_CHANNELS.forEach((channel, i) => {
    const v = data[MOUTH_OFFSET + i]!;
    if (v !== 0) { mouth[channel] = v / 255; hasMouth = true; }
  });
  const scale = PARTICIPANT_EXPRESSION_HEAD_LIMIT_DEG / 127;
  const signed = (byte: number): number => (byte > 127 ? byte - 256 : byte);
  const base = HEAD_OFFSET;
  return {
    weights,
    mouth: hasMouth ? mouth : undefined,
    headYaw: signed(data[base]!) * scale,
    headPitch: signed(data[base + 1]!) * scale,
    headRoll: signed(data[base + 2]!) * scale,
    sequence: (data[2]! << 8) | data[3]!,
  };
};

/**
 * Whether `next` supersedes `previous`, accounting for the 16-bit wrap. A frame
 * more than half the sequence space ahead is treated as older, which is the
 * standard RTP-style comparison and cannot mistake a wrap for a reordering.
 */
export const isNewerExpressionSequence = (next: number, previous: number): boolean => {
  const diff = (next - previous + 65536) % 65536;
  return diff !== 0 && diff < 32768;
};
